import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Space, Avatar, message, Drawer, Tooltip } from 'antd';
import useSessionRefresh from '../../hooks/useSessionRefresh';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  TeamOutlined,
  ShopOutlined,
  MenuOutlined,
  BellOutlined,
  AppstoreOutlined,
  KeyOutlined,
  GlobalOutlined,
  DollarOutlined,
  FileTextOutlined,
  WalletOutlined,
  HistoryOutlined,
  FireOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import type { MenuProps } from 'antd';
import ReactCountryFlag from 'react-country-flag';
import { DashboardProvider } from '../../context/DashboardContext';
import SessionTimer from '../SessionTimer/SessionTimer';

const { Header, Sider, Content } = Layout;

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
  gold: '#D4AF37',
  goldDim: 'rgba(212, 175, 55, 0.15)',
  goldHover: 'rgba(212, 175, 55, 0.25)',
};

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: ${COLORS.bgPrimary};
`;

const StyledHeader = styled(Header)`
  background: ${COLORS.bgSecondary} !important;
  border-bottom: 1px solid ${COLORS.border};
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  img {
    width: 32px;
    height: 32px;
  }
  
  .logo-text {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.accent};
    
    @media (max-width: 640px) {
      display: none;
    }
  }
  
  .badge {
    font-size: 12px;
    font-family: monospace;
    color: #6b7280;
    background: #0D1117;
    padding: 2px 8px;
    border-radius: 6px;
    border: 1px solid ${COLORS.border};
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

const RightSection = styled(Space)`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 12px;
    
    .logout-button {
      width: 44px !important;
      height: 44px !important;
      font-size: 20px !important;
      min-width: 44px !important;
    }
  }
`;

const NotificationBtn = styled.button`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${COLORS.hover};
    color: ${COLORS.textPrimary};
  }
  
  .count {
    position: absolute;
    top: 0;
    right: 0;
    background: #dc2626;
    color: white;
    font-size: 10px;
    font-weight: bold;
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${COLORS.bgSecondary};
  }
`;


const StyledMenuLink = styled(Link)`
  color: ${COLORS.textPrimary} !important;
  text-decoration: none;
  display: block;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${COLORS.gold} !important;
  }
  
  &:active {
    color: ${COLORS.gold} !important;
  }
`;

const StyledSider = styled(Sider)`
  background: ${COLORS.bgSecondary} !important;
  border-right: 1px solid ${COLORS.border};
  overflow-y: auto;
  height: calc(100vh - 64px);
  position: sticky;
  top: 64px;
  
  @media (max-width: 1024px) {
    display: none !important;
  }
  
  .ant-menu {
    border-right: none;
    background: transparent !important;
    padding: 16px 8px;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-menu-item {
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px;
    margin: 4px 0 !important;
    transition: all 0.2s ease;
    
    a {
      color: ${COLORS.textPrimary} !important;
      text-decoration: none;
    }
    
    &:hover {
      background: ${COLORS.goldDim} !important;
      color: ${COLORS.gold} !important;
      
      a {
        color: ${COLORS.gold} !important;
      }
    }
    
    &.ant-menu-item-selected {
      background: ${COLORS.goldDim} !important;
      color: ${COLORS.gold} !important;
      border-left: 3px solid ${COLORS.gold} !important;
      
      &::after {
        display: none !important;
      }
      
      a {
        color: ${COLORS.gold} !important;
        font-weight: 500;
      }
    }
  }
  
  .ant-menu-submenu {
    .ant-menu-submenu-title {
      color: ${COLORS.textPrimary} !important;
      border-radius: 8px;
      margin: 4px 0 !important;
      transition: all 0.2s ease;
      
      .ant-menu-submenu-arrow {
        color: ${COLORS.textSecondary} !important;
        transition: all 0.2s ease;
        
        &::before,
        &::after {
          background: ${COLORS.textSecondary} !important;
        }
      }
      
      &:hover {
        background: ${COLORS.goldDim} !important;
        color: ${COLORS.gold} !important;
        
        .ant-menu-submenu-arrow {
          color: ${COLORS.gold} !important;
          
          &::before,
          &::after {
            background: ${COLORS.gold} !important;
          }
        }
      }
    }
    
    &.ant-menu-submenu-open {
      .ant-menu-submenu-title {
        color: ${COLORS.gold} !important;
        
        .ant-menu-submenu-arrow {
          color: ${COLORS.gold} !important;
          
          &::before,
          &::after {
            background: ${COLORS.gold} !important;
          }
        }
      }
    }
    
    .ant-menu {
      background: ${COLORS.bgPrimary} !important;
      padding-left: 8px;
      
      .ant-menu-item {
        margin-left: 0 !important;
        padding-left: 40px !important;
        
        &:hover {
          background: ${COLORS.goldHover} !important;
        }
        
        &.ant-menu-item-selected {
          background: ${COLORS.goldDim} !important;
          border-left: 3px solid ${COLORS.gold} !important;
        }
      }
    }
  }
  
  .ant-menu-item-icon {
    color: inherit !important;
  }
`;

const StyledContent = styled(Content)`
  padding: 24px;
  overflow-y: auto;
  background: ${COLORS.bgPrimary};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const StyledFooter = styled.footer`
  background: ${COLORS.bgSecondary};
  border-top: 1px solid ${COLORS.border};
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    gap: 12px;
  }
`;

const SecurityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #10B981;
  font-weight: 500;
  
  .anticon {
    font-size: 14px;
  }
`;

const MobileMenuBtn = styled(Button)`
  display: none !important;
  border: none;
  background: transparent;
  color: ${COLORS.textPrimary};
  font-size: 20px;
  padding: 4px;
  
  @media (max-width: 1024px) {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }
  
  &:hover, &:focus {
    color: ${COLORS.accent} !important;
    background: ${COLORS.hover} !important;
  }
`;

const MerchantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>(['dashboard']);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [sessionExpiryTime, setSessionExpiryTime] = useState<number | null>(null);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);

  useEffect(() => {
    // Check if user has both accessToken and accessSecret - if not, redirect to login
    const accessToken = localStorage.getItem('accessToken');
    const accessSecret = localStorage.getItem('accessSecret');

    if (!accessToken || !accessSecret) {
      message.warning('Please log in to access your dashboard.');
      navigate('/?userType=Merchant');
      return;
    }

    // Close mobile menu when route changes
    setMobileMenuVisible(false);

    // Update selected menu item based on current route
    const path = location.pathname;
    if (path.includes('/profile')) {
      setSelectedKeys(['profile']);
    } else if (path.includes('/company')) {
      setSelectedKeys(['company']);
    } else if (path.includes('/staff/pending')) {
      setSelectedKeys(['staff-pending']);
    } else if (path.includes('/staff')) {
      setSelectedKeys(['staff']);
    } else if (path.includes('/api-keys')) {
      setSelectedKeys(['api-keys']);
    } else if (path.includes('/settings')) {
      setSelectedKeys(['settings']);
    } else if (path.includes('/blockchain-networks')) {
      setSelectedKeys(['blockchain-networks']);
    } else if (path.includes('/currencies')) {
      setSelectedKeys(['currencies']);
    } else if (path.includes('/contract-addresses')) {
      setSelectedKeys(['contract-addresses']);
    } else if (path.includes('/supported-blockchain-networks')) {
      setSelectedKeys(['supported-blockchain-networks']);
    } else if (path.includes('/supported-currencies')) {
      setSelectedKeys(['supported-currencies']);
    } else if (path.includes('/supported-contract-addresses')) {
      setSelectedKeys(['supported-contract-addresses']);
    } else if (path.includes('/webhooks')) {
      setSelectedKeys(['webhooks']);
    } else if (path.includes('/critical-notifications/account-update-requests')) {
      setSelectedKeys(['update-requests']);
    } else if (path.includes('/critical-notifications')) {
      setSelectedKeys(['critical-notifications']);
    } else if (path.includes('/internal-emails')) {
      setSelectedKeys(['internal-emails']);
    } else if (path.includes('/wallets')) {
      setSelectedKeys(['wallets']);
    } else if (path.includes('/gas-station')) {
      setSelectedKeys(['gas-station']);
    } else if (path.includes('/login-history')) {
      setSelectedKeys(['login-history']);
    } else {
      setSelectedKeys(['dashboard']);
    }
  }, [location.pathname, navigate]);

  // Fetch user profile and session expiry
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_ACCOUNT_PROFILE);
        if (response.data?.status === 'success' && response.data?.data) {
          setUserProfile(response.data.data);
        }
      } catch (error: any) {
        console.error('Failed to fetch user profile:', error);
      }
    };

    // Get session expiry time from localStorage
    const expiryTime = localStorage.getItem('sessionExpiryTime');
    if (expiryTime) {
      setSessionExpiryTime(parseInt(expiryTime, 10));
    }

    const fetchNotificationCount = async () => {
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.MERCHANT_GET_CRITICAL_NOTIFICATIONS,
          { params: { pageId: 1, perPage: 1 } }
        );
        if (response.data?.status === 'success' && response.data?.data) {
          const data = response.data.data;
          const paginator = data.paginator || data.pagination;
          const total =
            paginator?.itemCount ||
            paginator?.totalCount ||
            paginator?.total ||
            (data.itemsList || []).length ||
            0;
          setNotificationCount(total);
        }
      } catch (error: any) {
        console.error('Failed to fetch merchant notification count:', error);
      }
    };

    fetchUserProfile();
    fetchNotificationCount();
  }, []);

  const handleSessionExpire = () => {
    message.warning('Your session has expired. Please log in again.');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessSecret');
    localStorage.removeItem('userType');
    localStorage.removeItem('sessionExpiryTime');
    navigate('/?userType=Merchant');
  };

  // Periodically refresh the session token to keep Redis accessSecret alive
  useSessionRefresh('Merchant', handleSessionExpire);

  const handleLogout = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.LOGOUT_MERCHANT);
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('accessSecret');
      localStorage.removeItem('userType');
      localStorage.removeItem('sessionExpiryTime');
      
      // Capture current route (pathname + search + hash) for continue redirect
      const currentRoute = location.pathname + location.search + location.hash;
      // Encode the route to handle special characters
      const continueParam = encodeURIComponent(currentRoute);
      navigate(`/?userType=Merchant&continue=${continueParam}`);
    }
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const key = e.key;
    setMobileMenuVisible(false);
    
    if (key === 'logout') {
      handleLogout();
      return;
    }

    if (key === 'dashboard') {
      navigate('/igwena');
    } else if (key === 'profile') {
      navigate('/igwena/profile');
    } else if (key === 'company') {
      navigate('/igwena/company');
    } else if (key === 'staff') {
      navigate('/igwena/staff');
    } else if (key === 'staff-pending') {
      navigate('/igwena/staff/pending');
    } else if (key === 'api-keys') {
      navigate('/igwena/api-keys');
    } else if (key === 'webhooks') {
      navigate('/igwena/webhooks');
    } else if (key === 'critical-notifications') {
      navigate('/igwena/critical-notifications');
    } else if (key === 'update-requests') {
      navigate('/igwena/critical-notifications/account-update-requests');
    } else if (key === 'wallets') {
      navigate('/igwena/wallets');
    } else if (key === 'sweep-list') {
      navigate('/igwena/sweep-list');
    } else if (key === 'blockchain-networks') {
      navigate('/igwena/blockchain-networks');
    } else if (key === 'currencies') {
      navigate('/igwena/currencies');
    } else if (key === 'contract-addresses') {
      navigate('/igwena/contract-addresses');
    } else if (key === 'supported-blockchain-networks') {
      navigate('/igwena/supported-blockchain-networks');
    } else if (key === 'supported-currencies') {
      navigate('/igwena/supported-currencies');
    } else if (key === 'supported-contract-addresses') {
      navigate('/igwena/supported-contract-addresses');
    } else if (key === 'login-history') {
      navigate('/igwena/login-history');
    } else if (key === 'settings') {
      navigate('/igwena/settings');
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard',
      icon: <AppstoreOutlined />,
      label: <StyledMenuLink to="/igwena" onClick={(e) => e.stopPropagation()}>Dashboard</StyledMenuLink>,
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: <StyledMenuLink to="/igwena/profile" onClick={(e) => e.stopPropagation()}>Profile</StyledMenuLink>,
    },
    {
      key: 'company',
      icon: <ShopOutlined />,
      label: <StyledMenuLink to="/igwena/company" onClick={(e) => e.stopPropagation()}>Company Profile</StyledMenuLink>,
    },
    {
      key: 'staff',
      icon: <TeamOutlined />,
      label: 'Staff Management',
      children: [
        {
          key: 'staff',
          label: <StyledMenuLink to="/igwena/staff" onClick={(e) => e.stopPropagation()}>All Staff</StyledMenuLink>,
        },
        {
          key: 'staff-pending',
          label: <StyledMenuLink to="/igwena/staff/pending" onClick={(e) => e.stopPropagation()}>Pending Staff</StyledMenuLink>,
        },
        {
          key: 'login-history',
          icon: <HistoryOutlined />,
          label: <StyledMenuLink to="/igwena/login-history" onClick={(e) => e.stopPropagation()}>Login History</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'api-keys',
      icon: <KeyOutlined />,
      label: <StyledMenuLink to="/igwena/api-keys" onClick={(e) => e.stopPropagation()}>API Keys</StyledMenuLink>,
    },
    {
      key: 'webhooks',
      icon: <BellOutlined />,
      label: <StyledMenuLink to="/igwena/webhooks" onClick={(e) => e.stopPropagation()}>Webhooks</StyledMenuLink>,
    },
    {
      key: 'critical-notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
      children: [
        {
          key: 'critical-notifications',
          label: <StyledMenuLink to="/igwena/critical-notifications" onClick={(e) => e.stopPropagation()}>Critical Notifications</StyledMenuLink>,
        },
        {
          key: 'update-requests',
          label: <StyledMenuLink to="/igwena/critical-notifications/account-update-requests" onClick={(e) => e.stopPropagation()}>Update Requests</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'internal-emails',
      icon: <MailOutlined />,
      label: <StyledMenuLink to="/igwena/internal-emails" onClick={(e) => e.stopPropagation()}>Internal Emails</StyledMenuLink>,
    },
    {
      key: 'billing',
      icon: <DollarOutlined />,
      label: <StyledMenuLink to="/igwena/billing" onClick={(e) => e.stopPropagation()}>Billing & Payments</StyledMenuLink>,
    },
    {
      key: 'wallets',
      icon: <WalletOutlined />,
      label: <StyledMenuLink to="/igwena/wallets" onClick={(e) => e.stopPropagation()}>Wallets</StyledMenuLink>,
    },
    {
      key: 'sweep-list',
      icon: <WalletOutlined />,
      label: <StyledMenuLink to="/igwena/sweep-list" onClick={(e) => e.stopPropagation()}>Sweep/Transactions</StyledMenuLink>,
    },
    {
      key: 'gas-station',
      icon: <FireOutlined />,
      label: <StyledMenuLink to="/igwena/gas-station" onClick={(e) => e.stopPropagation()}>Gas Station</StyledMenuLink>,
    },
    {
      key: 'public-data',
      icon: <GlobalOutlined />,
      label: 'Public Data',
      children: [
        {
          key: 'blockchain-networks',
          icon: <GlobalOutlined />,
          label: <StyledMenuLink to="/igwena/blockchain-networks" onClick={(e) => e.stopPropagation()}>Blockchain Networks</StyledMenuLink>,
        },
        {
          key: 'currencies',
          icon: <DollarOutlined />,
          label: <StyledMenuLink to="/igwena/currencies" onClick={(e) => e.stopPropagation()}>Currencies</StyledMenuLink>,
        },
        {
          key: 'contract-addresses',
          icon: <FileTextOutlined />,
          label: <StyledMenuLink to="/igwena/contract-addresses" onClick={(e) => e.stopPropagation()}>Contract Addresses</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'supported-resources',
      icon: <ShopOutlined />,
      label: 'Supported Resources',
      children: [
        {
          key: 'supported-blockchain-networks',
          icon: <GlobalOutlined />,
          label: <StyledMenuLink to="/igwena/supported-blockchain-networks" onClick={(e) => e.stopPropagation()}>Blockchains</StyledMenuLink>,
        },
        {
          key: 'supported-currencies',
          icon: <DollarOutlined />,
          label: <StyledMenuLink to="/igwena/supported-currencies" onClick={(e) => e.stopPropagation()}>Currencies</StyledMenuLink>,
        },
        {
          key: 'supported-contract-addresses',
          icon: <FileTextOutlined />,
          label: <StyledMenuLink to="/igwena/supported-contract-addresses" onClick={(e) => e.stopPropagation()}>Contracts</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: <StyledMenuLink to="/igwena/settings" onClick={(e) => e.stopPropagation()}>Settings</StyledMenuLink>,
    },
  ];

  // Add logout to mobile menu only
  const mobileMenuItems: MenuProps['items'] = [
    ...menuItems,
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  return (
    <DashboardProvider value={{ sessionExpiryTime, onSessionExpire: handleSessionExpire, userProfile }}>
      <StyledLayout>
        <StyledHeader>
          <LogoSection>
          <MobileMenuBtn 
            icon={<MenuOutlined />} 
            onClick={() => setMobileMenuVisible(true)} 
          />
          <Logo>
            <img 
              src="https://ekumahost.net/publicImages/switch/SWITCHICON.svg" 
              alt="Switch Wallet"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="logo-text">Switch Wallet</span>
            <span className="badge">MERCHANT</span>
          </Logo>
        </LogoSection>

        <RightSection>
          {sessionExpiryTime && (
            <SessionTimer expiryTime={sessionExpiryTime} onExpire={handleSessionExpire} />
          )}
          <NotificationBtn onClick={() => navigate('/igwena/critical-notifications')}>
            <BellOutlined style={{ fontSize: '20px' }} />
            {notificationCount > 0 && (
              <span className="count">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </NotificationBtn>

          {userProfile && (
            <Tooltip 
              title={
                <div style={{ textAlign: 'right' }}>
                  <div>{userProfile.firstName} {userProfile.lastName}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>{userProfile.email}</div>
                </div>
              } 
              placement="bottomRight"
            >
              <div
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                onClick={() => navigate('/igwena/profile')}
              >
                {userProfile.countryCode && (
                  <ReactCountryFlag
                    countryCode={userProfile.countryCode}
                    svg
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                      borderRadius: '2px'
                    }}
                  />
                )}
                <Avatar
                  src={userProfile.portraitUrl ? `data:image/jpeg;base64,${userProfile.portraitUrl}` : undefined}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
                />
              </div>
            </Tooltip>
          )}
          
          <Tooltip title="Logout" placement="bottomRight">
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                color: COLORS.textSecondary,
                fontSize: '18px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
              className="logout-button"
            />
          </Tooltip>
        </RightSection>
      </StyledHeader>

      <Layout style={{ background: 'transparent', position: 'relative' }}>
        <StyledSider width={260}>
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </StyledSider>
        
        <StyledContent style={{ position: 'relative', zIndex: 1 }}>
          <Outlet />
        </StyledContent>
      </Layout>

      {/* Security Footer */}
      <StyledFooter>
        <SecurityBadge>
          <SafetyCertificateOutlined />
          Security Audit Passed - Jan 2026
        </SecurityBadge>
        <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
          © 2026 Switch Wallet. All rights reserved.
        </span>
      </StyledFooter>

      {/* Mobile Drawer Menu */}
      <Drawer
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width="85%"
        mask={true}
        maskClosable={true}
        closable={true}
        zIndex={1000}
        bodyStyle={{ 
          padding: 0, 
          background: 'transparent',
        }}
        headerStyle={{
          background: 'transparent',
          borderBottom: `1px solid ${COLORS.border}`,
          color: COLORS.textPrimary
        }}
        drawerStyle={{
          background: 'transparent',
          zIndex: 1001,
          maxWidth: '320px'
        }}
        maskStyle={{
          zIndex: 999
        }}
        title={
          <Logo>
            <img 
              src="https://ekumahost.net/publicImages/switch/SWITCHICON.svg" 
              alt="Switch Wallet"
              style={{ width: 24, height: 24 }}
            />
            <span className="logo-text" style={{ fontSize: 16 }}>Switch Wallet</span>
          </Logo>
        }
      >
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={mobileMenuItems}
          onClick={handleMenuClick}
          style={{ background: 'transparent', border: 'none' }}
        />
      </Drawer>
    </StyledLayout>
    </DashboardProvider>
  );
};

export default MerchantDashboard;
